import type { UserInfo, ManuallyInfoContext, ManuallyAddressContext } from "@src/types";
import { documentToHash } from "@core/utils";

type UserInfoContext = {
  meta: {
    user_info: UserInfo
  }
}

// list of domains to avoid cors erros to fetch VTEX IO endpoint
export const PROD_DOMAINS: Record<string, Record<string, string>> = {
  "lojaanimale": {
    "store": "https://lojaanimale.myvtex.com",
    "checkout": "https://secure.animale.com.br",
  },
  "lojafarm": {
    "store": "https://lojafarm.myvtex.com",
    "checkout": "https://lojafarm.myvtex.com",
  },
  "mariafilo": {
    "store": "https://mariafilo.myvtex.com",
    "checkout": "https://secure.mariafilo.com.br",
  },
  "lojacrisbarros": {
    "store": "https://lojacrisbarros.myvtex.com",
    "checkout": "https://secure.crisbarros.com.br",
  },
  "lojaoffpremium": {
    "store": "https://lojaoffpremium.myvtex.com",
    "checkout": "https://secure.offpremium.com.br",
  },
  "carolbassi": {
    "store": "https://carolbassi.myvtex.com",
    "checkout": "https://secure.carolbassi.com.br",
  },
  "bynv": {
    "store": "https://bynv.myvtex.com",
    "checkout": "https://bynv.myvtex.com",
  },
  "lojafabula": {
    "store": "https://lojafabula.myvtex.com",
    "checkout": "https://secure.afabula.com.br",
  },
  "lojausereserva": {
    "store": "https://lojausereserva.myvtex.com",
    "checkout": "https://lojausereserva.myvtex.com",
  },
  "foxton": {
    "store": "https://foxton.myvtex.com",
    "checkout": "https://secure.foxtonbrasil.com.br",
  },
};

type MasterDataSection = Record<string, unknown> | null | undefined
type MasterDataResponse = {
  customer?: MasterDataSection
  address?: MasterDataSection
} & Record<string, unknown>

const isFilled = (value: unknown) => value !== undefined && value !== null && value !== ''

const getFormFieldFilled = <T extends Record<string, unknown>>(source?: T): Partial<T> => {
  if (!source) return {}

  return Object.entries(source).reduce<Record<string, unknown>>((acc, [key, value]) => {
    if (isFilled(value)) acc[key] = value

    return acc
  }, {}) as Partial<T>
}

const buildCustomer = async (base: MasterDataSection, manuallyInfo?: ManuallyInfoContext) => {
  const { address: _address, cpf, phone, ...personal } = manuallyInfo ?? {}

  const customer: Record<string, unknown> = { ...(base ?? {}), ...getFormFieldFilled(personal) }

  // the forms have a single phone input; Master Data splits it in two
  if (isFilled(phone)) {
    customer.phone = phone
    customer.homePhone = phone
  }

  const rawDocument = (isFilled(cpf) ? cpf : customer.document) as string | undefined
  if (!isFilled(rawDocument)) return customer

  const document = rawDocument?.replace(/\D/g, '')
  return { ...customer, document, cpf: await documentToHash(document) }
}

const buildAddress = (base: MasterDataSection, manuallyAddress?: ManuallyAddressContext) => {
  const patch = getFormFieldFilled(manuallyAddress)

  if (!base && !Object.keys(patch).length) return null

  return { ...(base ?? {}), ...patch }
}

const buildUserInfo = async (base: MasterDataResponse, manuallyInfo?: ManuallyInfoContext) => ({
  ...base,
  customer: await buildCustomer(base?.customer, manuallyInfo),
  address: buildAddress(base?.address, manuallyInfo?.address),
})

export async function getUserInfo(context: UserInfoContext) {
  const userInfo = context?.meta?.user_info
  const manuallyInfo = userInfo?.manuallyInfo
  const domain = PROD_DOMAINS[userInfo?.account]?.[userInfo?.platform]

  // fallback base: all we know without Master Data
  const identity: MasterDataResponse = {
    address: null,
    customer: {
      id: userInfo?.userId,
      email: userInfo?.userEmail,
    },
  }

  if (!domain) return buildUserInfo(identity, manuallyInfo)

  try {
    const api = `${domain}/_v/user-info`
    const response = await fetch(api, {
      method: 'POST',
      body: JSON.stringify({ email: userInfo?.userEmail })
    })

    if (response.status !== 200) return buildUserInfo(identity, manuallyInfo)

    const res: MasterDataResponse = await response.json()

    return buildUserInfo(res, manuallyInfo)
  } catch (_) {
    return buildUserInfo(identity, manuallyInfo)
  }
}
